/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { waitFor } from '@ember/test-waiters';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import type { MultiFileUploadResult } from 'uplisting-frontend/components/ui/multi-file-upload/index';

const ACCEPTED_FILE_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'image/jpeg',
  'image/png',
];

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB

interface IArgs {
  properties: ModelRegistry['property'][];
  preselectedPropertyId?: string;
  isGlobal?: boolean;
  onClose(): void;
  onFileUploaded?(): void;
}

interface FileUploadPanelSignature {
  Element: HTMLDivElement;
  Args: IArgs;
}

export default class UiKnowledgeCenterFileUploadPanelComponent extends Component<FileUploadPanelSignature> {
  @service store!: Services['store'];
  @service sentry!: Services['sentry'];
  @service notifications!: Services['notifications'];

  @tracked _selectedPropertyId?: string;
  @tracked _hasUserSelected = false;
  @tracked uploadedFiles: ModelRegistry['ai-knowledge-base-file'][] = [];

  acceptedTypes = ACCEPTED_FILE_TYPES;
  maxFileSize = MAX_FILE_SIZE;

  get selectedPropertyId(): string | undefined {
    if (this._hasUserSelected) return this._selectedPropertyId;

    return this.args.preselectedPropertyId ?? this._selectedPropertyId;
  }

  get isGlobal(): boolean {
    return this.args.isGlobal ?? false;
  }

  get isUploadReady(): boolean {
    return this.isGlobal || !!this.selectedPropertyId;
  }

  get selectedProperty(): ModelRegistry['property'] | undefined {
    if (!this.selectedPropertyId) return undefined;

    return this.args.properties.find(
      (prop) => prop.id === this.selectedPropertyId,
    );
  }

  @action
  handlePropertyChange(property?: ModelRegistry['property']): void {
    this._selectedPropertyId = property?.id;
    this._hasUserSelected = true;
  }

  @action
  @waitFor
  async handleFileUploaded(result: MultiFileUploadResult): Promise<void> {
    try {
      const record = this.store.createRecord('ai-knowledge-base-file', {
        fileName: result.fileName,
        fileType: result.fileType,
        fileUrl: result.s3Url,
        fileSize: result.fileSize,
        property: this.selectedProperty,
      });

      await record.save();

      this.uploadedFiles = [...this.uploadedFiles, record];
      this.notifications.info('knowledge_center.file_upload.upload_success');
      this.args.onFileUploaded?.();
    } catch (error) {
      this.sentry.captureException(error);
      this.notifications.error('knowledge_center.file_upload.upload_failed');
    }
  }

  @action
  @waitFor
  async handleDeleteFile(
    file: ModelRegistry['ai-knowledge-base-file'],
  ): Promise<void> {
    try {
      await file.destroyRecord();

      this.uploadedFiles = this.uploadedFiles.filter((f) => f !== file);
    } catch (error) {
      this.sentry.captureException(error);
      this.notifications.error();
    }
  }

  @action
  handleClose(): void {
    this.args.onClose();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::KnowledgeCenter::FileUploadPanel': typeof UiKnowledgeCenterFileUploadPanelComponent;
  }
}
